import { Fragment, useMemo } from "react"
import type { FC, ReactElement } from "react"

import { type QueryKey } from "@tanstack/react-query"
import { AnimatePresence, LazyMotion, domAnimation } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"

import {
    HelpHintExtended,
    useSessionInfoAttendeeListPrefetcher,
    useSessionInfoRenderPropsFunctions
} from "@/3514/components"
import { EAppAnimationName } from "@/3514/store/app"
import {
    EUserRole,
    type ICoachingSessionsQueryModel,
    type IUserCoachModel,
    type IUserParticipantModel
} from "@/3514/types"
import { buildCssClassName } from "@/3514/utils"
import {
    type TEmptyAsyncCallback,
    type TEmptyRenderCallback,
    emptyAsyncCallback,
    emptyRenderCallback
} from "@/shared/types/functions"

import List from "./components/coaching-sessions.list.component"
import BlockTitle from "./elements/coaching-sessions.block-title.element"
import classes from "./styles"

function renderUpcomingEmptyMessage(): ReactElement {
    return (
        <HelpHintExtended
            textKey="entities.sessions.upcomingSessions.emptyMessage"
            containerClassName={classes.entity.emptyMessageContainer()}
            withBreakLineOnMobile
        />
    )
}
function renderPastEmptyMessage(): ReactElement {
    return (
        <HelpHintExtended
            textKey="entities.sessions.pastSessions.emptyMessage"
            containerClassName={classes.entity.emptyMessageContainer()}
            withBreakLineOnMobile
        />
    )
}
function renderUpcomingSessionTitle(): ReactElement {
    return <BlockTitle textKey="entities.sessions.upcomingSessions.title" />
}
function renderPastSessionsTitle(): ReactElement {
    return <BlockTitle textKey="entities.sessions.pastSessions.title" />
}

type TCoachingSessionsViewProps = {
    sessions: ICoachingSessionsQueryModel
    sessionsUpdatedAt: number
    user: IUserCoachModel | IUserParticipantModel
    sessionsQueryKey: QueryKey
    sessionInfoRole: EUserRole | undefined
    onLiveSessionStarted: TEmptyAsyncCallback
    onLiveSessionPassed: TEmptyAsyncCallback
    wrapperClassName?: string
    withPostSessionModalOpeners: boolean
    withSwappableSessions: boolean
    withGiftSessions: boolean
    withAttendanceMarkableSessions: boolean
    renderScheduler: TEmptyRenderCallback
    renderModals: TEmptyRenderCallback
}
const CoachingSessionsView: FC<TCoachingSessionsViewProps> = ({
    sessions,
    sessionsUpdatedAt,
    user,
    sessionsQueryKey,
    sessionInfoRole,
    onLiveSessionStarted,
    onLiveSessionPassed,
    wrapperClassName,
    withPostSessionModalOpeners,
    withSwappableSessions,
    withGiftSessions,
    withAttendanceMarkableSessions,
    renderScheduler,
    renderModals
}: TCoachingSessionsViewProps): ReactElement => {
    const {
        renderButtonsProps: renderSessionButtonsProps,
        renderMessagesProps: renderSessionMessagesProps,
        renderActionsProps: renderSessionActionsProps
    }: ReturnType<typeof useSessionInfoRenderPropsFunctions> = useSessionInfoRenderPropsFunctions(sessionInfoRole)

    const onAttendeesListHover: ReturnType<typeof useSessionInfoAttendeeListPrefetcher> =
        useSessionInfoAttendeeListPrefetcher()

    const isSectionVisible: boolean = useMemo(
        (): boolean =>
            !(
                isEmpty(sessions.unscheduledSessions) &&
                isEmpty(sessions.upcomingSessions) &&
                isEmpty(sessions.pastSessions) &&
                isEmpty(sessions.liveSession)
            ),
        [sessions]
    )

    const isEmptyLiveSession: boolean = isEmpty(sessions.liveSession)

    return (
        <Fragment>
            <AnimatePresence initial={false} mode="sync">
                {isSectionVisible && (
                    <section
                        className={buildCssClassName(classes.entity.section(), {
                            [wrapperClassName]: wrapperClassName
                        })}
                    >
                        <LazyMotion features={domAnimation} strict>
                            {renderScheduler()}

                            <List
                                user={user}
                                sessionsUpdatedAt={sessionsUpdatedAt}
                                sessions={isEmptyLiveSession ? [] : [sessions.liveSession]}
                                sessionsQueryKey={sessionsQueryKey}
                                sessionInfoRole={sessionInfoRole}
                                animationKey={EAppAnimationName.CoachingSessions_LiveSession}
                                animationDelay={0}
                                expandedAccordionIndexes={[0]}
                                containerClassName={classes.entity.listLive()}
                                onLiveSessionStarted={emptyAsyncCallback}
                                onLiveSessionPassed={onLiveSessionPassed}
                                onAttendeesListHover={onAttendeesListHover}
                                renderTitle={emptyRenderCallback}
                                renderEmptyMessage={emptyRenderCallback}
                                renderAccordionButtonsProps={renderSessionButtonsProps}
                                renderAccordionActionsProps={renderSessionActionsProps}
                                renderAccordionMessagesProps={renderSessionMessagesProps}
                                withSkippedRenderOnEmptyItems
                                withReschedulableSessions={false}
                                withGiftSessions={withGiftSessions}
                                withAttendanceMarkableSessions={withAttendanceMarkableSessions}
                                withSwappableSessions={withSwappableSessions}
                                withPostSessionModalOpeners={false}
                                withSessionsLiveChecker
                            />

                            <List
                                user={user}
                                sessionsUpdatedAt={sessionsUpdatedAt}
                                sessions={sessions.upcomingSessions}
                                sessionsQueryKey={sessionsQueryKey}
                                sessionInfoRole={sessionInfoRole}
                                animationKey={EAppAnimationName.CoachingSessions_UpcomingSessions}
                                animationDelay={isEmptyLiveSession ? 0 : 0.1}
                                expandedAccordionIndexes={undefined}
                                containerClassName={classes.entity.listGeneral()}
                                onLiveSessionStarted={onLiveSessionStarted}
                                onLiveSessionPassed={emptyAsyncCallback}
                                onAttendeesListHover={onAttendeesListHover}
                                renderTitle={renderUpcomingSessionTitle}
                                renderEmptyMessage={renderUpcomingEmptyMessage}
                                renderAccordionButtonsProps={renderSessionButtonsProps}
                                renderAccordionActionsProps={renderSessionActionsProps}
                                renderAccordionMessagesProps={renderSessionMessagesProps}
                                withSkippedRenderOnEmptyItems={false}
                                withReschedulableSessions
                                withGiftSessions={withGiftSessions}
                                withAttendanceMarkableSessions={withAttendanceMarkableSessions}
                                withSwappableSessions={withSwappableSessions}
                                withPostSessionModalOpeners={false}
                                withSessionsLiveChecker
                            />

                            <List
                                user={user}
                                sessionsUpdatedAt={sessionsUpdatedAt}
                                sessions={sessions.pastSessions}
                                sessionsQueryKey={sessionsQueryKey}
                                sessionInfoRole={sessionInfoRole}
                                animationKey={EAppAnimationName.CoachingSessions_PastSessions}
                                animationDelay={isEmptyLiveSession ? 0.1 : 0.2}
                                expandedAccordionIndexes={undefined}
                                containerClassName={classes.entity.listGeneral()}
                                onLiveSessionStarted={emptyAsyncCallback}
                                onLiveSessionPassed={emptyAsyncCallback}
                                onAttendeesListHover={onAttendeesListHover}
                                renderTitle={renderPastSessionsTitle}
                                renderEmptyMessage={renderPastEmptyMessage}
                                renderAccordionButtonsProps={renderSessionButtonsProps}
                                renderAccordionActionsProps={renderSessionActionsProps}
                                renderAccordionMessagesProps={renderSessionMessagesProps}
                                withSkippedRenderOnEmptyItems={false}
                                withReschedulableSessions={false}
                                withGiftSessions={withGiftSessions}
                                withAttendanceMarkableSessions={withAttendanceMarkableSessions}
                                withSwappableSessions={false}
                                withPostSessionModalOpeners={withPostSessionModalOpeners}
                                withSessionsLiveChecker={false}
                            />
                        </LazyMotion>
                    </section>
                )}
            </AnimatePresence>

            {renderModals()}
        </Fragment>
    )
}

CoachingSessionsView.displayName = "CoachingSessionsView"

export default CoachingSessionsView
